/**
 * Fleet Vehicle List Component
 * ============================
 * Displays a filterable, sortable list of fleet vehicles with quick actions.
 */

import React, { useState, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  Search,
  Filter,
  Plus,
  ChevronDown,
  ChevronUp,
  MoreVertical,
  Truck,
  AlertTriangle,
  CheckCircle,
  Clock,
  Wrench,
  Download,
  Upload,
  Eye,
  Edit,
  Trash2,
  UserPlus,
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fleetApi } from '../../services/fleetApi';

// Status Badge Component
const StatusBadge = ({ status }) => {
  const config = {
    active: { bg: 'bg-success-bg', text: 'text-success', label: 'Active' },
    maintenance: { bg: 'bg-warning-bg', text: 'text-warning', label: 'Maintenance' },
    out_of_service: { bg: 'bg-danger-bg', text: 'text-danger', label: 'Out of Service' },
    disposed: { bg: 'bg-surface-muted', text: 'text-ink-soft', label: 'Disposed' },
  };

  const { bg, text, label } = config[status] || config.active;

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-small font-medium ${bg} ${text}`}>
      {label}
    </span>
  );
};

// Service Status Indicator
const ServiceStatus = ({ vehicle }) => {
  if (vehicle.is_service_overdue) {
    return (
      <div className="flex items-center text-danger">
        <AlertTriangle className="w-4 h-4 mr-1" />
        <span className="text-small">Overdue</span>
      </div>
    );
  }
  if (vehicle.days_until_service && vehicle.days_until_service <= 7) {
    return (
      <div className="flex items-center text-warning">
        <Clock className="w-4 h-4 mr-1" />
        <span className="text-small">{vehicle.days_until_service}d</span>
      </div>
    );
  }
  return (
    <div className="flex items-center text-success">
      <CheckCircle className="w-4 h-4 mr-1" />
      <span className="text-small">OK</span>
    </div>
  );
};

// Vehicle Row Component
const VehicleRow = ({ vehicle, onAction }) => {
  const [showMenu, setShowMenu] = useState(false);

  return (
    <tr className="hover:bg-surface-muted transition-colors">
      {/* Unit Number */}
      <td className="px-6 py-4 whitespace-nowrap">
        <Link
          to={`vehicles/${vehicle.id}`}
          className="text-sm font-medium text-accent hover:text-accent-hover"
        >
          {vehicle.unit_number || '-'}
        </Link>
      </td>

      {/* Vehicle */}
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          <div className="flex-shrink-0 h-10 w-10 bg-surface-muted rounded-lg flex items-center justify-center">
            <Truck className="w-5 h-5 text-ink-muted" />
          </div>
          <div className="ml-4">
            <div className="text-sm font-medium text-ink-soft">
              {vehicle.vehicle_profile?.display_name || 'Unknown'}
            </div>
            <div className="text-small text-ink-muted">
              VIN: ...{vehicle.vehicle_profile?.vin?.slice(-6) || 'N/A'}
            </div>
          </div>
        </div>
      </td>

      {/* Status */}
      <td className="px-6 py-4 whitespace-nowrap">
        <StatusBadge status={vehicle.status} />
      </td>

      {/* Department */}
      <td className="px-6 py-4 whitespace-nowrap text-sm text-ink-muted">
        {vehicle.department?.name || '-'}
      </td>

      {/* Driver */}
      <td className="px-6 py-4 whitespace-nowrap text-sm text-ink-muted">
        {vehicle.assigned_driver?.display_name || '-'}
      </td>

      {/* Service Status */}
      <td className="px-6 py-4 whitespace-nowrap">
        <ServiceStatus vehicle={vehicle} />
      </td>

      {/* Odometer */}
      <td className="px-6 py-4 whitespace-nowrap text-sm text-ink-muted">
        {vehicle.current_odometer 
          ? `${vehicle.current_odometer.toLocaleString()} ${vehicle.odometer_unit}`
          : '-'}
      </td>

      {/* Actions */}
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
        <div className="relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-1 rounded-lg hover:bg-surface-muted"
          >
            <MoreVertical className="w-5 h-5 text-ink-muted" />
          </button>

          {showMenu && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowMenu(false)}
              />
              <div className="absolute right-0 mt-2 w-48 bg-surface-card rounded-lg shadow-popover border border-border-default py-1 z-20">
                <Link
                  to={`vehicles/${vehicle.id}`}
                  className="flex items-center px-4 py-2 text-sm text-ink-soft hover:bg-surface-muted"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  View Details
                </Link>
                <button
                  onClick={() => onAction('edit', vehicle)}
                  className="w-full flex items-center px-4 py-2 text-sm text-ink-soft hover:bg-surface-muted"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </button>
                <button
                  onClick={() => onAction('assign', vehicle)}
                  className="w-full flex items-center px-4 py-2 text-sm text-ink-soft hover:bg-surface-muted"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Assign Driver
                </button>
                <button
                  onClick={() => onAction('service', vehicle)}
                  className="w-full flex items-center px-4 py-2 text-sm text-ink-soft hover:bg-surface-muted"
                >
                  <Wrench className="w-4 h-4 mr-2" />
                  Log Service
                </button>
                <hr className="my-1" />
                <button
                  onClick={() => onAction('delete', vehicle)}
                  className="w-full flex items-center px-4 py-2 text-sm text-danger hover:bg-danger-bg"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Remove
                </button>
              </div>
            </>
          )}
        </div>
      </td>
    </tr>
  );
};

// Filter Dropdown
const FilterDropdown = ({ label, options, value, onChange }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={`inline-flex items-center px-3 py-2 border rounded-lg text-sm font-medium ${
          value
            ? 'border-accent text-accent bg-accent/10'
            : 'border-border-default text-ink-soft bg-surface-card hover:bg-surface-muted'
        }`}
      >
        <Filter className="w-4 h-4 mr-2" />
        {label}
        {value && `: ${options.find(o => o.value === value)?.label}`}
        <ChevronDown className="w-4 h-4 ml-2" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute left-0 mt-2 w-48 bg-surface-card rounded-lg shadow-popover border border-border-default py-1 z-20">
            <button
              onClick={() => { onChange(null); setOpen(false); }}
              className={`w-full px-4 py-2 text-sm text-left hover:bg-surface-muted ${!value ? 'bg-surface-page font-medium' : ''}`}
            >
              All
            </button>
            {options.map((option) => (
              <button
                key={option.value}
                onClick={() => { onChange(option.value); setOpen(false); }}
                className={`w-full px-4 py-2 text-sm text-left hover:bg-surface-muted ${
                  value === option.value ? 'bg-accent/10 text-accent font-medium' : ''
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// Main Component
const FleetVehicleList = () => {
  const { fleetId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // State
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(null);
  const [departmentFilter, setDepartmentFilter] = useState(null);
  const [serviceFilter, setServiceFilter] = useState(null);
  const [sortBy, setSortBy] = useState('unit_number');
  const [sortOrder, setSortOrder] = useState('asc');
  const [page, setPage] = useState(1);
  const pageSize = 25;

  // Fetch vehicles
  const { data, isLoading, error } = useQuery({
    queryKey: ['fleetVehicles', fleetId, { search, statusFilter, departmentFilter, serviceFilter, sortBy, sortOrder, page }],
    queryFn: () => fleetApi.listVehicles(fleetId, {
      search,
      status: statusFilter,
      department_id: departmentFilter,
      is_service_overdue: serviceFilter === 'overdue' ? true : undefined,
      has_upcoming_service: serviceFilter === 'due_soon' ? true : undefined,
      sort_by: sortBy,
      sort_order: sortOrder,
      page,
      page_size: pageSize,
    }),
  });

  // Fetch departments for filter
  const { data: departments } = useQuery({
    queryKey: ['fleetDepartments', fleetId],
    queryFn: () => fleetApi.listDepartments(fleetId),
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (vehicleId) => fleetApi.removeVehicle(fleetId, vehicleId),
    onSuccess: () => {
      queryClient.invalidateQueries(['fleetVehicles', fleetId]);
    },
  });

  // Handle actions
  const handleAction = (action, vehicle) => {
    switch (action) {
      case 'edit':
        navigate(`vehicles/${vehicle.id}/edit`);
        break;
      case 'assign':
        // Open assign driver modal
        break;
      case 'service':
        navigate(`vehicles/${vehicle.id}/service/new`);
        break;
      case 'delete':
        if (window.confirm(`Remove ${vehicle.unit_number || vehicle.vehicle_profile?.display_name} from fleet?`)) {
          deleteMutation.mutate(vehicle.id);
        }
        break;
    }
  };

  // Handle sort
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  // Sort header component
  const SortHeader = ({ column, children }) => (
    <th
      className="px-6 py-3 text-left text-small font-medium text-ink-muted uppercase tracking-wider cursor-pointer hover:text-ink-soft"
      onClick={() => handleSort(column)}
    >
      <div className="flex items-center">
        {children}
        {sortBy === column && (
          sortOrder === 'asc' ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />
        )}
      </div>
    </th>
  );

  // Status filter options
  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'maintenance', label: 'In Maintenance' },
    { value: 'out_of_service', label: 'Out of Service' },
  ];

  // Service filter options
  const serviceOptions = [
    { value: 'overdue', label: 'Service Overdue' },
    { value: 'due_soon', label: 'Service Due Soon' },
  ];

  // Department filter options
  const departmentOptions = useMemo(() => {
    return (departments || []).map(d => ({ value: d.id, label: d.name }));
  }, [departments]);

  return (
    <div className="min-h-screen bg-surface-page">
      {/* Header */}
      <div className="bg-surface-card border-b border-border-default">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-ink-soft">Fleet Vehicles</h1>
              <p className="mt-1 text-sm text-ink-muted">
                {data?.total || 0} vehicles total
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <Link
                to="import"
                className="inline-flex items-center px-4 py-2 border border-border-default text-sm font-medium rounded-lg text-ink-soft bg-surface-card hover:bg-surface-muted"
              >
                <Upload className="w-4 h-4 mr-2" />
                Bulk Import
              </Link>
              <button
                onClick={() => {/* Export CSV */}}
                className="inline-flex items-center px-4 py-2 border border-border-default text-sm font-medium rounded-lg text-ink-soft bg-surface-card hover:bg-surface-muted"
              >
                <Download className="w-4 h-4 mr-2" />
                Export
              </button>
              <Link
                to="add"
                className="inline-flex items-center px-4 py-2 bg-accent text-white text-sm font-medium rounded-lg hover:bg-accent-hover"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Vehicle
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex flex-wrap items-center gap-3">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-ink-muted" />
            <input
              type="text"
              placeholder="Search by unit #, VIN, or plate..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-border-strong rounded-lg text-sm focus:ring-2 focus:ring-accent focus:border-accent"
            />
          </div>

          {/* Filters */}
          <FilterDropdown
            label="Status"
            options={statusOptions}
            value={statusFilter}
            onChange={setStatusFilter}
          />
          <FilterDropdown
            label="Department"
            options={departmentOptions}
            value={departmentFilter}
            onChange={setDepartmentFilter}
          />
          <FilterDropdown
            label="Service"
            options={serviceOptions}
            value={serviceFilter}
            onChange={setServiceFilter}
          />

          {/* Clear filters */}
          {(search || statusFilter || departmentFilter || serviceFilter) && (
            <button
              onClick={() => {
                setSearch('');
                setStatusFilter(null);
                setDepartmentFilter(null);
                setServiceFilter(null);
              }}
              className="text-sm text-ink-muted hover:text-ink-soft"
            >
              Clear all
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-surface-card rounded-lg border border-border-default overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent" />
            </div>
          ) : error ? (
            <div className="p-6 text-center text-danger">
              Error loading vehicles: {error.message}
            </div>
          ) : data?.vehicles?.length === 0 ? (
            <div className="p-12 text-center">
              <Truck className="w-12 h-12 text-ink-muted mx-auto mb-4" />
              <h3 className="text-lg font-medium text-ink-soft mb-2">No vehicles found</h3>
              <p className="text-ink-muted mb-4">
                {search || statusFilter || departmentFilter || serviceFilter
                  ? 'Try adjusting your filters'
                  : 'Get started by adding your first vehicle'}
              </p>
              <Link
                to="add"
                className="inline-flex items-center px-4 py-2 bg-accent text-white text-sm font-medium rounded-lg hover:bg-accent-hover"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Vehicle
              </Link>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-border-default">
                  <thead className="bg-surface-page">
                    <tr>
                      <SortHeader column="unit_number">Unit #</SortHeader>
                      <th className="px-6 py-3 text-left text-small font-medium text-ink-muted uppercase tracking-wider">
                        Vehicle
                      </th>
                      <SortHeader column="status">Status</SortHeader>
                      <th className="px-6 py-3 text-left text-small font-medium text-ink-muted uppercase tracking-wider">
                        Department
                      </th>
                      <th className="px-6 py-3 text-left text-small font-medium text-ink-muted uppercase tracking-wider">
                        Driver
                      </th>
                      <th className="px-6 py-3 text-left text-small font-medium text-ink-muted uppercase tracking-wider">
                        Service
                      </th>
                      <SortHeader column="current_odometer">Odometer</SortHeader>
                      <th className="px-6 py-3 text-right text-small font-medium text-ink-muted uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-surface-card divide-y divide-border-default">
                    {data?.vehicles?.map((vehicle) => (
                      <VehicleRow
                        key={vehicle.id}
                        vehicle={vehicle}
                        onAction={handleAction}
                      />
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {data?.total > pageSize && (
                <div className="px-6 py-4 border-t border-border-default flex items-center justify-between">
                  <p className="text-sm text-ink-muted">
                    Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, data.total)} of {data.total} vehicles
                  </p>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="px-3 py-1 border border-border-default rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-surface-muted"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setPage(p => p + 1)}
                      disabled={page * pageSize >= data.total}
                      className="px-3 py-1 border border-border-default rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-surface-muted"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FleetVehicleList;
